import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';
import withWidth from '@material-ui/core/withWidth';

import { getFormValues } from 'redux-form';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';
import Filter from 'components/common/Filter';
import TabNavigation from 'components/common/TabNavigation';
import DocumentCategoriesTable from 'components/settings/DocumentCategoriesTable';
import DocumentCategoryCreateEditDialog from 'components/settings/DocumentCategoryCreateEditDialog';

import {
  fetchDocumentCategories,
  clearDocumentCategories,
} from 'redux/modules/documentCategories';

import { getFilterChangeTypes } from 'utils/filterChangeTypes';
import objectDiff from 'utils/objectDiff';
import throttle from 'utils/throttle';

import { appTitle, titleSeparator } from 'config/helmetSettings';

class WorkspaceDocumentCategories extends Component {
  constructor(props) {
    super(props);

    this.state = {
      createDialogOpen: false,
      mobileFilterExpanded: ['xs', 'sm'].indexOf(props.width) === -1,
      limit: 10,
      offset: 0,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onFilterButtonClick = this.onFilterButtonClick.bind(this);
    this.onCreateDialogClose = this.onCreateDialogClose.bind(this);
    this.onMobileFilterBottonClick = this.onMobileFilterBottonClick.bind(this);
    this.onTableScroll = this.onTableScroll.bind(this);

    this.onThrottledRequest = throttle(this.onRequest, 2000, false, true);
  }

  componentWillMount() {
    this.props.actions.clearDocumentCategories();
  }

  componentDidMount() {
    this.onRequest();
  }

  componentWillReceiveProps(nextProps) {
    const { filters, width } = this.props;
    const { limit, offset } = this.state;
    const filtersChanged = nextProps.filters.Name !== filters.Name;
    let newOffset = offset;
    if (filtersChanged) {
      newOffset = 0;
    }
    if (newOffset !== offset) {
      this.setState({ offset: newOffset });
    }

    if (nextProps.width !== width) {
      this.setState({
        mobileFilterExpanded: ['xs', 'sm'].indexOf(nextProps.width) === -1,
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return ((Object.keys(objectDiff(this.props, nextProps)).length +
      Object.keys(objectDiff(this.state, nextState)).length) > 0);
  }

  componentDidUpdate(prevProps, prevState) {
    const { filters, documentCategories, count, loading } = this.props;
    const { limit, offset } = this.state;
    const limitChanged = prevState.limit !== limit;
    const offsetChanged = prevState.offset !== offset;
    const { throttledFiltersChanged, filtersChanged } = getFilterChangeTypes(
      prevProps.filters,
      filters,
      {
        textFilterKeys: ['Name'],
      },
    );

    if (throttledFiltersChanged) {
      this.onThrottledRequest();
    } else if (limitChanged || offsetChanged || filtersChanged) {
      this.onRequest();
    } else if (documentCategories.length < count
      && this.scrollRef
      && this.scrollRef.scrollTop === 0
      && loading === 0) {
      this.onTableScroll({ target: this.scrollRef });
    }
  }

  onRequest() {
    const { actions, filters } = this.props;
    const { limit, offset } = this.state;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map(key => ({
        name: key,
        operation: 'contains',
        val: filters[key],
        case_sensitivity: false,
      }))
      : null;
    actions.fetchDocumentCategories({
      limit,
      offset,
      filters: requestFilters,
      triggerLoading: true,
      push: offset > 0,
    });
  }

  onFilterButtonClick() {
    this.setState({ createDialogOpen: true });
  }

  onCreateDialogClose() {
    this.setState({ createDialogOpen: false });
  }

  onMobileFilterBottonClick() {
    this.setState({ mobileFilterExpanded: !this.state.mobileFilterExpanded });
  }

  onTableScroll(event) {
    const { offset, limit } = this.state;

    if ((event.target.offsetHeight + event.target.scrollTop) / event.target.scrollHeight > 0.9) {
      if ((this.props.documentCategories.length - limit) >= offset) {
        this.setState({
          offset: offset + limit,
        });
      }
    }
  }

  render() {
    const { documentCategories, count, loading, filters } = this.props;
    const { createDialogOpen, mobileFilterExpanded } = this.state;
    return (
      <Layout variant="settings">
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Document Categories
          </title>
        </Helmet>
        <TabNavigation
          items={[
            {
              text: 'Note Categories',
              href: '/settings/workspace/note-categories',
            },
            {
              text: 'Document Categories',
              href: '/settings/workspace/document-categories',
            },
            {
              text: 'Workflow Statuses',
              href: '/settings/workspace/workflow-statuses',
            },
            {
              text: 'Claim Statuses',
              href: '/settings/workspace/claim-statuses',
            },
            {
              text: 'Claim Codes',
              href: '/settings/workspace/claim-adjustment-codes',
            },
          ]}
        />
        <Filter
          form="documentCategoriesFilter"
          placeholder="Search Document Categories"
          primaryButtonText="Add New"
          onPrimaryButtonClick={this.onFilterButtonClick}
          onMobileFilterBottonClick={this.onMobileFilterBottonClick}
          mobileFilterExpanded={mobileFilterExpanded}
          filters={filters}
          showTableCount
          count={count}
          loading={loading}
          items={documentCategories}
          beforeTable
        />
        {loading > 0 &&
          <Progress fixed />
        }
        <DocumentCategoriesTable
          items={documentCategories}
          count={count}
          loading={loading}
          mobileFilterExpanded={mobileFilterExpanded}
          onScroll={this.onTableScroll}
          scrollRef={(scrollRef) => { this.scrollRef = scrollRef; }}
        />
        <DocumentCategoryCreateEditDialog
          form="documentCategoryCreate"
          open={createDialogOpen}
          onClose={this.onCreateDialogClose}
          onSubmitSuccess={() => {
            this.onCreateDialogClose();
            this.onRequest();
          }}
        />
      </Layout>
    );
  }
}

WorkspaceDocumentCategories.propTypes = {
  documentCategories: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    Name: PropTypes.string,
  }).isRequired,
  actions: PropTypes.shape({
    fetchDocumentCategories: PropTypes.func.isRequired,
    clearDocumentCategories: PropTypes.func.isRequired,
  }).isRequired,
  width: PropTypes.string.isRequired,
};

WorkspaceDocumentCategories.defaultProps = {};

const mapStateToProps = (store) => {
  const documentCategoriesFilterValues = getFormValues('documentCategoriesFilter')(store);
  return {
    documentCategories: store.documentCategories.data,
    count: store.documentCategories.count,
    filters: {
      ...documentCategoriesFilterValues
        && documentCategoriesFilterValues.Filter
        ? { Name: documentCategoriesFilterValues.Filter }
        : {},
    },
    loading: store.documentCategories.loading,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchDocumentCategories,
    clearDocumentCategories,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withWidth()(WorkspaceDocumentCategories),
);



// WEBPACK FOOTER //
// ./src/pages/settings/WorkspaceDocumentCategories.js